import React, { useEffect, useState } from 'react';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IListingData } from '@models/listing';
import { headers } from './content';
import { useLazyQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import Loader from '@components/loader/Loader';
import { mapToListingData } from './utils';
import ListingTable from '@components/table/ListingTable';
import { getCertificatePolicyDetailsQuery } from './queries';
import { enumListAsRecordObject } from '@utils/graph-utils';
import { IAbstractRecord } from '@models';
import { businessPartnerQuery } from '../../../queries';
import PrecisionService from '@services/PrecisionService';

interface ICertificatePaymentTermsSchedule {
  policyId: string;
}

const CertificatePaymentTermsSchedule: React.FC<
  ICertificatePaymentTermsSchedule
> = ({ policyId }) => {
  const [booted, setBooted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 5,
    pageNumber: 0,
    totalCount: 0,
  });

  const [getPolicyBillsLazy] = useLazyQuery(getCertificatePolicyDetailsQuery());
  const [getBusinessPartnerDetailsLazy] = useLazyQuery(businessPartnerQuery);
  const [getPolicyDetailsLazy] = useLazyQuery(
    getCertificatePolicyDetailsQuery()
  );

  const loadData = async () => {
    setLoading(true);

    try {
      const result = await getPolicyBillsLazy({
        variables: {
          policyId: policyId,
        },
      });

      if (result.error) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        return;
      }

      const data = result.data;

      const categories = enumListAsRecordObject(
        data?.Accounting_ParentBillCategoryList?.enumValues
      );

      const newTableData = mapToListingData(
        data?.Accounting?.queries?.GetPolicyCertificatesBills,
        categories
      );
      setTableData({ ...newTableData });
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setLoading(false);
      setBooted(true);
    }

    try {
      const policyDetailsResult = await getPolicyDetailsLazy({
        variables: {
          policyId,
        },
      });

      if (policyDetailsResult.error) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        return;
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const initialize = async () => {
    await loadData();
  };

  useEffect(() => {
    initialize();
  }, []);

  async function handleTooltipFetch(id: string): Promise<IAbstractRecord> {
    const popoverRows: IAbstractRecord = {};
    await getBusinessPartnerDetailsLazy({
      variables: {
        parentBillID: id,
      },
    }).then((response) => {
      response?.data?.Accounting?.queries?.GetBusinessPartnersCommissions?.forEach(
        (obj: IAbstractRecord) => {
          popoverRows[obj.businessPartner_Id] = {
            BusinessPartner: obj.businessPartner_FullName,
            Share: PrecisionService.multiplyBy100(
              Number(obj.accounting_Bills_CommissionPercentage)
            ),
            Commission: obj.accounting_Bills_TotalCommission,
            Currency: obj.accounting_Bills_Currency?.Symbol,
          };
        }
      );
    });
    return popoverRows;
  }

  headers.commission.tooltipSettings.tooltipFetch = handleTooltipFetch;

  if (!booted) {
    return <Loader />;
  }

  return (
    <ListingTable
      inlineTitle="Certificates Payment Terms Schedule"
      name="certificatepaymentTermsSchedule"
      data={tableData}
      headers={headers}
      loader={loading}
      actions={[]}
      inline
      orderByAscendingByDefault
      disableSelection
    />
  );
};

export default CertificatePaymentTermsSchedule;
